import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";

import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
	chip: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		padding: "5px 10px",
		borderRadius: 9,
		border: `1px solid ${theme.custom.border}`,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(148,163,184,0.08)"
				: "rgba(15,23,42,0.03)",
		cursor: "pointer",
		transition: "all .15s ease",
		userSelect: "none",
		"&:hover": {
			borderColor: theme.palette.primary.main,
			backgroundColor:
				theme.palette.type === "dark"
					? "rgba(5,150,105,0.14)"
					: "rgba(5,150,105,0.07)",
		},
	},
	copied: {
		borderColor: theme.palette.success.main,
		backgroundColor: "rgba(16,185,129,0.12)",
	},
	value: {
		fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
		fontSize: "0.82rem",
		fontWeight: 600,
		letterSpacing: "0.04em",
		color: theme.palette.text.primary,
		lineHeight: 1,
	},
	icon: {
		fontSize: 14,
		color: theme.custom.textSecondary,
		transition: "color .15s ease",
	},
	iconOk: {
		fontSize: 14,
		color: theme.palette.success.main,
	},
}));

// O ID do RustDesk e um numero longo; exibir em grupos de 3 facilita a
// leitura em voz alta ao telefone com o cliente. O valor COPIADO continua
// sendo o original, sem espacos.
const formatId = (id) =>
	String(id || "").replace(/(\d{3})(?=\d)/g, "$1 ");

const DeviceId = ({ value }) => {
	const classes = useStyles();
	const [copied, setCopied] = useState(false);
	const timer = useRef(null);

	useEffect(() => () => clearTimeout(timer.current), []);

	const handleCopy = async (e) => {
		e.stopPropagation();
		try {
			// clipboard.writeText exige contexto seguro (https); o fallback
			// cobre acesso por http em rede local.
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(value);
			} else {
				const el = document.createElement("textarea");
				el.value = value;
				el.style.position = "fixed";
				el.style.opacity = "0";
				document.body.appendChild(el);
				el.select();
				document.execCommand("copy");
				document.body.removeChild(el);
			}
			setCopied(true);
			clearTimeout(timer.current);
			timer.current = setTimeout(() => setCopied(false), 1600);
		} catch {
			// silencioso: se falhar, o usuario ainda pode selecionar o texto
		}
	};

	return (
		<Tooltip
			title={
				copied
					? i18n.t("flydeskDevices.idCopied")
					: i18n.t("flydeskDevices.copyId")
			}
			placement="top"
			arrow
		>
			<span
				className={`${classes.chip} ${copied ? classes.copied : ""}`}
				onClick={handleCopy}
			>
				<span className={classes.value}>{formatId(value)}</span>
				{copied ? (
					<CheckIcon className={classes.iconOk} />
				) : (
					<FileCopyOutlinedIcon className={classes.icon} />
				)}
			</span>
		</Tooltip>
	);
};

export default DeviceId;
